import { useState, useCallback, useMemo, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { PortalAuthContext } from './usePortalAuth';

const STORAGE_KEY_JWT = 'portal_jwt';
const STORAGE_KEY_INVESTOR = 'portal_investor_info';

function readStoredSession() {
  try {
    const token = localStorage.getItem(STORAGE_KEY_JWT);
    const info = localStorage.getItem(STORAGE_KEY_INVESTOR);
    if (token && info) return { token, info: JSON.parse(info) };
  } catch { /* corrupted storage — ignore */ }
  return null;
}

function writeStoredSession(token, info) {
  try {
    localStorage.setItem(STORAGE_KEY_JWT, token);
    localStorage.setItem(STORAGE_KEY_INVESTOR, JSON.stringify(info));
  } catch { /* storage full or unavailable — ignore */ }
}

function clearStoredSession() {
  try {
    localStorage.removeItem(STORAGE_KEY_JWT);
    localStorage.removeItem(STORAGE_KEY_INVESTOR);
  } catch { /* ignore */ }
}

export default function PortalAuthProvider() {
  const stored = readStoredSession();
  const [jwt, setJwt] = useState(stored?.token ?? null);
  const [investorInfo, setInvestorInfo] = useState(stored?.info ?? null);
  const [loading, setLoading] = useState(!!stored);
  const location = useLocation();

  // Validate stored token on mount
  useEffect(() => {
    if (!stored) return;

    let cancelled = false;
    async function validate() {
      try {
        const res = await fetch('/api/investor-auth-validate', {
          headers: { Authorization: `Bearer ${stored.token}` },
        });
        if (!res.ok) throw new Error('invalid');
      } catch {
        if (!cancelled) {
          clearStoredSession();
          setJwt(null);
          setInvestorInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    validate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((token, info) => {
    writeStoredSession(token, info);
    setJwt(token);
    setInvestorInfo(info);
  }, []);

  const logout = useCallback(() => {
    clearStoredSession();
    setJwt(null);
    setInvestorInfo(null);
  }, []);

  const getJwt = useCallback(() => jwt, [jwt]);

  const isAuthenticated = !!jwt;

  const value = useMemo(() => ({
    jwt,
    investorInfo,
    login,
    logout,
    getJwt,
    isAuthenticated,
  }), [jwt, investorInfo, login, logout, getJwt, isAuthenticated]);

  // Allow login and auth verify pages without auth
  const publicPaths = ['/portal/login', '/portal/auth'];
  const isPublicPath = publicPaths.some(p => location.pathname.startsWith(p));

  if (loading && !isPublicPath) {
    // Revalidating a stored session. Nothing here to skeleton — the route below
    // has not been chosen yet — so it says what is happening instead of spinning.
    return (
      <div className="min-h-screen bg-activ-navy flex items-center justify-center px-4">
        <p className="text-sm text-white/70">Signing you in&hellip;</p>
      </div>
    );
  }

  if (!isAuthenticated && !isPublicPath) {
    return <Navigate to="/portal/login" replace />;
  }

  return (
    <PortalAuthContext.Provider value={value}>
      <Outlet />
    </PortalAuthContext.Provider>
  );
}
